<script setup lang="ts">
import type { EmbedEvent, PrefillAndIframeAttrsConfig, GlobalCalWithoutNs } from '@calcom/embed-core';

interface InitConfig {
    debug?: boolean;
    uiDebug?: boolean;
}

export interface CalcomUiConfig {
    hideEventTypeDetails?: boolean;
    theme?: 'dark' | 'light' | 'auto' | null;
    cssVarsPerTheme?: Record<string, Record<string, string>>;
    layout?: 'month_view' | 'week_view' | 'column_view';
    colorScheme?: string | null;
}

type Props = {
    link: string;
    initConfig?: InitConfig;
    config?: PrefillAndIframeAttrsConfig;
    uiConfig?: CalcomUiConfig;
};

// PROPS
const { link, config, initConfig = {}, uiConfig } = defineProps<Props>();

// EMITS
const emit = defineEmits<{
    bookingSuccessfulV2: [EmbedEvent<'bookingSuccessfulV2'>];
    rescheduleBookingSuccessfulV2: [EmbedEvent<'rescheduleBookingSuccessfulV2'>];
    linkFailed: [EmbedEvent<'linkFailed'>];
}>();

if (!link) {
    throw new Error('calLink is required');
}

// COMPOSABLES
const url = computed(() => new URL(link));
const embedJsUrl = computed<string>(() => `//${url.value.host}/embed/embed.js`);
const namespace = computed<string>(() => url.value.pathname.split('/')[1]);
const calOrigin = computed<string>(() => `${url.value.origin}/`);
const calLink = computed<string>(() => url.value.pathname);
const Cal = useCalcomEmbed(embedJsUrl);
const calcom = useTemplateRef<HTMLDivElement>('calcom');

// DATA
const initialized = ref(false);

// HANDLERS
const onBookingSuccessfulV2 = (e: EmbedEvent<'bookingSuccessfulV2'>): void => emit('bookingSuccessfulV2', e);
const onRescheduleBookingSuccessfulV2 = (e: EmbedEvent<'rescheduleBookingSuccessfulV2'>): void => emit('rescheduleBookingSuccessfulV2', e);
const onLinkFailed = (e: EmbedEvent<'linkFailed'>): void => emit('linkFailed', e);
// WATCHERS
type WatcherProps = [GlobalCalWithoutNs | undefined, string, PrefillAndIframeAttrsConfig | undefined, string, string, InitConfig | undefined];
watch(
    (): WatcherProps => [Cal.value, calLink.value, config, namespace.value, calOrigin.value, initConfig],
    ([Cal, calLink, config, namespace, calOrigin, initConfig]: WatcherProps): void => {
        if (!Cal || initialized.value || !calcom.value) {
            return;
        }

        initialized.value = true;

        Cal('init', namespace, {
            ...initConfig,
            origin: calOrigin,
        });
        Cal('inline', {
            elementOrSelector: calcom.value,
            calLink,
            config,
        });
        uiConfig && Cal('ui', uiConfig);
        Cal('on', {
            action: 'bookingSuccessfulV2',
            callback: onBookingSuccessfulV2,
        });
        Cal('on', {
            action: 'rescheduleBookingSuccessfulV2',
            callback: onRescheduleBookingSuccessfulV2,
        });
        Cal('on', {
            action: 'linkFailed',
            callback: onLinkFailed,
        });
    }
);
onBeforeUnmount(() => {
    if (!initialized.value || !Cal.value) {
        return;
    }

    Cal.value('off', {
        action: 'bookingSuccessfulV2',
        callback: onBookingSuccessfulV2,
    });
    Cal.value('off', {
        action: 'rescheduleBookingSuccessfulV2',
        callback: onRescheduleBookingSuccessfulV2,
    });
    Cal.value('off', {
        action: 'linkFailed',
        callback: onLinkFailed,
    });
});
</script>

<template>
    <div ref="calcom" />
</template>
